import { FileExcelOutlined, ReloadOutlined, CalculatorOutlined } from '@ant-design/icons';
import {
    Button,
    Card,
    Col,
    DatePicker,
    message,
    Row,
    Segmented,
    Space,
    Statistic,
    Switch,
    Table,
    Tabs,
    Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { parseContentDispositionFilename } from '../domain/downloads';
import useRequest from '../hooks/useRequest';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

type Row = Record<string, unknown>;
type RcStyle = 'detaliat' | 'sumat' | 'lunar';

/** Tabs that filter by a single date; the rest filter by a [from, to] range. */
const SINGLE_DATE_TABS = new Set([
    'registru-casa',
    'centralizator-zilnic',
    'centralizator-lunar',
    'centralizator-lunar-sumar',
    'cassa',
    'rc',
]);

const ISO = 'YYYY-MM-DD';
const formatDate = (value: unknown): string =>
    value ? dayjs(value as string).format('DD/MM/YYYY') : '';
const formatMoney = (value: unknown): string => {
    if (value === null || value === undefined) {
        return '';
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed.toLocaleString(undefined, { maximumFractionDigits: 0 }) : '';
};
const formatQty = (value: unknown): string => {
    const parsed = Number(value ?? 0);
    return Number.isFinite(parsed)
        ? parsed.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : '';
};

interface RegistruCasaTotals {
    totalIncasare: number;
    totalPlata: number;
    sold: number;
}
interface ZilnicTotals {
    valoareContracte: number;
    totalComisioane: number;
    valoareBazaAchitate: number;
    totalVanzari: number;
}
interface RcSold {
    soldInitial: number;
    soldFinal: number;
}

const Contabilitate = () => {
    const { t, i18n } = useTranslation();
    const request = useRequest();
    const { token, API_BASE_URL } = useAuth();

    const [activeTab, setActiveTab] = useState('registru-casa');
    const [loading, setLoading] = useState(false);
    const [date, setDate] = useState<dayjs.Dayjs>(dayjs());
    const [range, setRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([dayjs(), dayjs()]);
    const [activeOnly, setActiveOnly] = useState(false);
    const [rcStyle, setRcStyle] = useState<RcStyle>('detaliat');

    const [rows, setRows] = useState<Row[]>([]);
    const [casaTotals, setCasaTotals] = useState<RegistruCasaTotals | null>(null);
    const [zilnic, setZilnic] = useState<{
        contracteIncheiate: Row[];
        contracteAchitate: Row[];
        comisioane: Row[];
        vanzari: Row[];
        totals: ZilnicTotals;
    } | null>(null);
    const [ape, setApe] = useState<Row[]>([]);
    const [rcSold, setRcSold] = useState<RcSold | null>(null);
    // Only the latest load may write state: a slower answer for the previous tab or filter
    // must not overwrite the rows of the one now on screen.
    const latestLoad = useRef(0);

    const usesSingleDate = SINGLE_DATE_TABS.has(activeTab);

    const queryParams = useCallback((): Record<string, string> => {
        if (usesSingleDate) {
            const params: Record<string, string> = { date: date.format(ISO) };
            if (activeTab === 'rc') {
                params.style = rcStyle;
            }
            return params;
        }
        const params: Record<string, string> = {
            from: range[0].format(ISO),
            to: range[1].format(ISO),
        };
        if (activeTab === 'registru-contracte') {
            params.activeOnly = String(activeOnly);
        }
        return params;
    }, [usesSingleDate, date, activeTab, rcStyle, range, activeOnly]);

    const loadTab = useCallback(async () => {
        latestLoad.current += 1;
        const load = latestLoad.current;
        setLoading(true);
        setCasaTotals(null);
        setZilnic(null);
        setApe([]);
        setRcSold(null);
        try {
            const result = await request<{
                success: boolean;
                data?: Row[];
                totals?: RegistruCasaTotals & ZilnicTotals;
                contracteIncheiate?: Row[];
                contracteAchitate?: Row[];
                comisioane?: Row[];
                vanzari?: Row[];
                activPasivExpirat?: Row[];
                sold?: RcSold;
            }>({ path: `/contabilitate/${activeTab}`, params: queryParams() });

            if (load !== latestLoad.current) {
                return;
            }
            if (!result.success) {
                setRows([]);
                message.error(t('contabilitate.loadError'));
                return;
            }
            setRows(result.data ?? []);
            if (activeTab === 'registru-casa' && result.totals) {
                setCasaTotals(result.totals);
            }
            if (activeTab === 'centralizator-zilnic' && result.totals) {
                setZilnic({
                    contracteIncheiate: result.contracteIncheiate ?? [],
                    contracteAchitate: result.contracteAchitate ?? [],
                    comisioane: result.comisioane ?? [],
                    vanzari: result.vanzari ?? [],
                    totals: result.totals,
                });
            }
            if (activeTab === 'centralizator-lunar') {
                setApe(result.activPasivExpirat ?? []);
            }
            if (activeTab === 'rc' && result.sold) {
                setRcSold(result.sold);
            }
        } catch (error) {
            if (load !== latestLoad.current) {
                return;
            }
            console.error('Contabilitate load error:', error);
            setRows([]);
            message.error(t('contabilitate.loadError'));
        } finally {
            if (load === latestLoad.current) {
                setLoading(false);
            }
        }
    }, [activeTab, queryParams, request, t]);

    useEffect(() => {
        loadTab();
    }, [loadTab]);

    const handleExport = useCallback(async () => {
        try {
            const params = new URLSearchParams({ ...queryParams(), lang: i18n.language });
            const response = await fetch(
                `${API_BASE_URL}/contabilitate/export/${activeTab}?${params.toString()}`,
                {
                    headers: {
                        ...(token && { Authorization: `Bearer ${token}` }),
                        Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                    },
                },
            );
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const anchor = document.createElement('a');
            anchor.href = url;
            anchor.download =
                parseContentDispositionFilename(response.headers.get('Content-Disposition')) ??
                `${activeTab}.xlsx`;
            anchor.click();
            URL.revokeObjectURL(url);
            message.success(t('contabilitate.exportSuccess'));
        } catch (error) {
            console.error('Contabilitate export error:', error);
            message.error(t('contabilitate.exportError'));
        }
    }, [API_BASE_URL, activeTab, i18n.language, queryParams, token, t]);

    const col = (key: string) => t(`contabilitate.col.${key}`);
    const moneyCol = (field: string) => ({
        title: col(field),
        dataIndex: field,
        key: field,
        align: 'right' as const,
        render: formatMoney,
    });
    const qtyCol = (field: string) => ({
        title: col(field),
        dataIndex: field,
        key: field,
        align: 'right' as const,
        render: formatQty,
    });
    const textCol = (field: string, render?: (v: unknown) => string) => ({
        title: col(field),
        dataIndex: field,
        key: field,
        ellipsis: true,
        ...(render ? { render } : {}),
    });
    const cassaDescriptionCol = {
        ...textCol('description'),
        render: (value: unknown) => {
            const description = typeof value === 'string' ? value : '';
            return ['Intrari', 'Iesiri'].includes(description) ? (
                <Text strong>{description}</Text>
            ) : (
                description
            );
        },
    };

    const columnsByTab = useMemo<Record<string, ColumnsType<Row>>>(
        () => ({
            'registru-casa': [
                textCol('date', formatDate),
                textCol('documentNumber'),
                textCol('description'),
                moneyCol('incasare'),
                moneyCol('plata'),
            ],
            'jurnal-vanzari': [
                textCol('date', formatDate),
                textCol('documentNumber'),
                textCol('clientName'),
                textCol('clientCode'),
                textCol('description'),
                textCol('contractNumber'),
                moneyCol('total'),
                moneyCol('baseAmount'),
                moneyCol('commission'),
                moneyCol('exchangeDiff'),
                moneyCol('bonus'),
                moneyCol('goodsSale'),
            ],
            'registru-contracte': [
                textCol('date', formatDate),
                textCol('contractNumber'),
                textCol('clientName'),
                moneyCol('value'),
                textCol('objectDescription'),
                qtyCol('gold'),
                qtyCol('silver'),
                moneyCol('electronics'),
                moneyCol('other'),
            ],
            'centralizator-lunar-sumar': [
                textCol('date', formatDate),
                moneyCol('value'),
                qtyCol('gold'),
                qtyCol('silver'),
                qtyCol('electronics'),
                qtyCol('other'),
            ],
            cassa: [cassaDescriptionCol, moneyCol('value')],
            rc: [
                textCol('date', formatDate),
                textCol('description'),
                moneyCol('intrari'),
                moneyCol('iesiri'),
            ],
            'anulari-stornari': [
                textCol('description'),
                moneyCol('amount'),
                textCol('date', formatDate),
                textCol('user'),
                textCol('stamp', formatDate),
            ],
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [i18n.language],
    );

    const incheiateColumns: ColumnsType<Row> = [
        textCol('nrCrt'),
        textCol('contractNumber'),
        textCol('clientName'),
        textCol('object'),
        textCol('unit'),
        qtyCol('quantity'),
        moneyCol('value'),
    ];
    const achitateColumns: ColumnsType<Row> = [
        textCol('nrCrt'),
        textCol('date', formatDate),
        textCol('invoiceNumber'),
        textCol('contractNumber'),
        textCol('clientName'),
        moneyCol('baseAmount'),
        moneyCol('commission'),
        moneyCol('bonus'),
    ];
    const vanzariColumns: ColumnsType<Row> = [
        textCol('nrCrt'),
        textCol('date', formatDate),
        textCol('invoiceNumber'),
        textCol('contractNumber'),
        moneyCol('baseAmount'),
        moneyCol('saleAmount'),
        moneyCol('difference'),
        moneyCol('vat'),
    ];
    const lunarColumns: ColumnsType<Row> = [
        textCol('nrCrt'),
        textCol('description'),
        moneyCol('value'),
        textCol('quantity'),
    ];

    const tableFor = (columns: ColumnsType<Row>, data: Row[]) => (
        <Table
            rowKey={(_, index) => String(index)}
            columns={columns}
            dataSource={data}
            loading={loading}
            size="small"
            pagination={{ pageSize: 25, showSizeChanger: true }}
            scroll={{ x: true }}
        />
    );

    const renderControls = () => (
        <Space wrap>
            {usesSingleDate ? (
                <>
                    <Text strong>{t('contabilitate.referenceDate')}:</Text>
                    <DatePicker
                        value={date}
                        onChange={value => value && setDate(value)}
                        format="DD/MM/YYYY"
                        allowClear={false}
                    />
                </>
            ) : (
                <>
                    <Text strong>{t('contabilitate.period')}:</Text>
                    <RangePicker
                        value={range}
                        onChange={value =>
                            value?.[0] && value[1] && setRange([value[0], value[1]])
                        }
                        format="DD/MM/YYYY"
                        allowClear={false}
                    />
                </>
            )}
            {activeTab === 'registru-contracte' && (
                <Space>
                    <Text>{t('contabilitate.activeOnly')}</Text>
                    <Switch checked={activeOnly} onChange={setActiveOnly} />
                </Space>
            )}
            {activeTab === 'rc' && (
                <Segmented
                    value={rcStyle}
                    onChange={value => setRcStyle(value as RcStyle)}
                    options={[
                        { label: t('contabilitate.styleDetaliat'), value: 'detaliat' },
                        { label: t('contabilitate.styleSumat'), value: 'sumat' },
                        { label: t('contabilitate.styleLunar'), value: 'lunar' },
                    ]}
                />
            )}
            <Button icon={<ReloadOutlined />} onClick={loadTab}>
                {t('contabilitate.refresh')}
            </Button>
            <Button type="primary" icon={<FileExcelOutlined />} onClick={handleExport}>
                {t('contabilitate.exportExcel')}
            </Button>
        </Space>
    );

    const renderCasaTotals = () =>
        casaTotals && (
            <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col xs={8}>
                    <Statistic title={t('contabilitate.totalIncasare')} value={casaTotals.totalIncasare} precision={0} />
                </Col>
                <Col xs={8}>
                    <Statistic title={t('contabilitate.totalPlata')} value={casaTotals.totalPlata} precision={0} />
                </Col>
                <Col xs={8}>
                    <Statistic title={t('contabilitate.sold')} value={casaTotals.sold} precision={0} />
                </Col>
            </Row>
        );

    const renderZilnic = () =>
        zilnic && (
            <>
                <Row gutter={16} style={{ marginBottom: 16 }}>
                    <Col xs={12} md={6}>
                        <Statistic title={t('contabilitate.valoareContracte')} value={zilnic.totals.valoareContracte} precision={0} />
                    </Col>
                    <Col xs={12} md={6}>
                        <Statistic title={t('contabilitate.totalComisioane')} value={zilnic.totals.totalComisioane} precision={0} />
                    </Col>
                    <Col xs={12} md={6}>
                        <Statistic title={t('contabilitate.valoareBazaAchitate')} value={zilnic.totals.valoareBazaAchitate} precision={0} />
                    </Col>
                    <Col xs={12} md={6}>
                        <Statistic title={t('contabilitate.totalVanzari')} value={zilnic.totals.totalVanzari} precision={0} />
                    </Col>
                </Row>
                <Title level={5}>{t('contabilitate.gridIncheiate')}</Title>
                {tableFor(incheiateColumns, zilnic.contracteIncheiate)}
                <Title level={5}>{t('contabilitate.gridAchitate')}</Title>
                {tableFor(achitateColumns, zilnic.contracteAchitate)}
                <Title level={5}>{t('contabilitate.gridComisioane')}</Title>
                {tableFor(achitateColumns, zilnic.comisioane)}
                <Title level={5}>{t('contabilitate.gridVanzari')}</Title>
                {tableFor(vanzariColumns, zilnic.vanzari)}
            </>
        );

    const renderLunar = () => (
        <>
            {tableFor(lunarColumns, rows)}
            <Title level={5} style={{ marginTop: 16 }}>
                {t('contabilitate.apeTitle')}
            </Title>
            {tableFor(lunarColumns, ape)}
        </>
    );

    const renderRcSold = () =>
        rcSold && (
            <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col xs={12}>
                    <Statistic title={t('contabilitate.soldInitial')} value={rcSold.soldInitial} precision={0} />
                </Col>
                <Col xs={12}>
                    <Statistic title={t('contabilitate.soldFinal')} value={rcSold.soldFinal} precision={0} />
                </Col>
            </Row>
        );

    let content: JSX.Element;
    if (activeTab === 'centralizator-zilnic') {
        content = renderZilnic() || <></>;
    } else if (activeTab === 'centralizator-lunar') {
        content = renderLunar();
    } else if (activeTab === 'registru-casa') {
        content = (
            <>
                {renderCasaTotals()}
                {tableFor(columnsByTab['registru-casa'], rows)}
            </>
        );
    } else if (activeTab === 'rc') {
        content = (
            <>
                {renderRcSold()}
                {tableFor(columnsByTab.rc, rows)}
            </>
        );
    } else {
        content = tableFor(columnsByTab[activeTab], rows);
    }

    const tabItems = [
        { key: 'registru-casa', label: t('contabilitate.tabs.registruCasa') },
        { key: 'jurnal-vanzari', label: t('contabilitate.tabs.jurnalVanzari') },
        { key: 'registru-contracte', label: t('contabilitate.tabs.registruContracte') },
        { key: 'centralizator-zilnic', label: t('contabilitate.tabs.centralizatorZilnic') },
        { key: 'centralizator-lunar', label: t('contabilitate.tabs.centralizatorLunar') },
        { key: 'centralizator-lunar-sumar', label: t('contabilitate.tabs.centralizatorLunarSumar') },
        { key: 'cassa', label: t('contabilitate.tabs.cassa') },
        { key: 'rc', label: t('contabilitate.tabs.rc') },
        { key: 'anulari-stornari', label: t('contabilitate.tabs.anulariStornari') },
    ];

    return (
        <div className="contabilitate-page">
            <div
                className="page-header"
                style={{ ['--page-header-accent' as string]: '#9333ea' } as React.CSSProperties}
            >
                <div className="page-header__lead">
                    <span className="page-header__icon">
                        <CalculatorOutlined aria-hidden />
                    </span>
                    <div>
                        <Title level={2} style={{ margin: 0 }}>
                            {t('contabilitate.title')}
                        </Title>
                        <Text type="secondary">{t('contabilitate.subtitle')}</Text>
                    </div>
                </div>
            </div>
            <Card style={{ marginBottom: 16 }}>{renderControls()}</Card>
            <Card>
                <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
                {content}
            </Card>
        </div>
    );
};

export default Contabilitate;
